import React, { useState, useEffect } from 'react';
import { distributionListsAPI, reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Mail, Users, Loader2, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';

const EmailReportModal = ({ 
  open, 
  onOpenChange, 
  report, 
  project, 
  onCreateList,
  userEmailSettings = {}
}) => {
  const [lists, setLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ccSelf, setCcSelf] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  useEffect(() => {
    if (open && project?.id) {
      loadLists();
      // Set default subject based on report status
      const statusLabels = { on_track: 'On Track', at_risk: 'At Risk', off_track: 'Off Track' };
      const statusLabel = statusLabels[report?.status] || 'Update';
      setSubject(`[${statusLabel}] ${project.name} - Status Report`);
      setCcSelf(userEmailSettings?.cc_self !== false);
    }
  }, [open, project?.id]);

  const loadLists = async () => {
    try {
      const response = await distributionListsAPI.list(project.id);
      setLists(response.data);
    } catch (error) {
      console.error('Failed to load distribution lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleList = (listId) => {
    setSelectedLists(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const getTotalRecipients = () => {
    const emails = new Set();
    selectedLists.forEach(listId => {
      const list = lists.find(l => l.id === listId);
      list?.recipients?.forEach(r => {
        if (r.email) emails.add(r.email.toLowerCase());
      });
    });
    return emails.size;
  };

  const handleSend = async () => {
    if (selectedLists.length === 0) {
      toast.error('Please select at least one distribution list');
      return;
    }

    setSending(true);
    try {
      const response = await reportsAPI.sendEmail(report.id, {
        distribution_list_ids: selectedLists,
        subject: subject || undefined,
        message: message || undefined
      });
      
      setSent(true);
      setSentCount(response.data.sent_count);
      toast.success(`Report sent to ${response.data.sent_count} recipients`);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
        setSent(false);
        setSelectedLists([]);
        setMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSent(false);
    setSelectedLists([]);
    setMessage('');
  };

  // Success state
  if (sent) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="py-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Email Sent!</h3>
            <p className="text-slate-500">
              Your report has been sent to {sentCount} recipient{sentCount !== 1 ? 's' : ''}.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Status Report
          </DialogTitle>
          <DialogDescription>
            Send this report to your stakeholders via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Distribution Lists Selection */}
          <div className="space-y-3">
            <Label>Select Distribution Lists</Label>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : lists.length === 0 ? (
              <div className="border border-dashed rounded-lg p-6 text-center">
                <AlertCircle className="h-8 w-8 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500 mb-3">No distribution lists yet</p>
                <Button variant="outline" size="sm" onClick={onCreateList}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Distribution List
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {lists.map((list) => {
                  const isSelected = selectedLists.includes(list.id);
                  return (
                    <div 
                      key={list.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleToggleList(list.id)}
                      onKeyDown={(e) => e.key === 'Enter' && handleToggleList(list.id)}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`select-list-${list.id}`}
                    >
                      <div 
                        className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${
                          isSelected 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-slate-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="h-3 w-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{list.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {list.recipients?.length || 0} recipients
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line"
              data-testid="email-subject-input"
            />
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Custom Message (optional)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to include in the email..."
              rows={3}
              data-testid="email-message-input"
            />
          </div>

          {/* CC Self */}
          <div className="flex items-center gap-2">
            <Checkbox 
              id="cc-self"
              checked={ccSelf}
              onCheckedChange={setCcSelf}
            />
            <Label htmlFor="cc-self" className="text-sm font-normal cursor-pointer">
              CC myself on this email
            </Label>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          {selectedLists.length > 0 && (
            <div className="text-sm text-slate-500 mr-auto">
              Sending to <strong>{getTotalRecipients()}</strong> recipient{getTotalRecipients() !== 1 ? 's' : ''}
            </div>
          )}
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend}
            disabled={selectedLists.length === 0 || sending}
            data-testid="send-email-btn"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailReportModal;
